import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/ledgerApi";
import { Loader2, Trash2, Plus, TrendingUp, TrendingDown, Scale, ChevronLeft, ChevronRight } from "lucide-react";
import { inr } from "@/components/ledger/BakeOrderBuilder";

const inputCls =
  "w-full h-12 px-4 rounded-[12px] bg-[rgba(240,233,216,0.05)] border border-[rgba(179,145,74,0.35)] font-body text-sm text-[var(--hoa-cream-50)] placeholder:text-[rgba(240,233,216,0.35)] focus:outline-none focus:border-[var(--hoa-gold-400)] transition-colors";

const MODES = ["Cash", "GPay", "Bank Transfer"];

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const monthNow = () => today().slice(0, 7);

const shiftMonth = (m, delta) => {
  const [y, mm] = m.split("-").map(Number);
  const d = new Date(y, mm - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (m) =>
  new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });

const fmtDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

function SummaryCard({ label, value, Icon, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex-1 min-w-[150px] rounded-[16px] border border-[rgba(179,145,74,0.3)] bg-[rgba(240,233,216,0.04)] p-4 sm:p-5"
    >
      <p className="flex items-center gap-1.5 font-label text-[9px] tracking-[0.22em] uppercase text-[rgba(240,233,216,0.55)]">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.8} style={{ color: accent }} /> {label}
      </p>
      <p className="font-display text-xl sm:text-2xl mt-2" style={{ color: accent }}>
        {inr(value)}
      </p>
    </div>
  );
}

export const Accounts = ({ onAuthError }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState("");
  const [type, setType] = useState("income");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("Cash");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(today());
  const [month, setMonth] = useState(monthNow());

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/admin/ledger");
      setData(res.data);
    } catch (e) {
      if (onAuthError) onAuthError(e);
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return;
    setSaving(true);
    try {
      await api.post("/admin/ledger", {
        type,
        amount: amt,
        mode: type === "income" ? mode : null,
        note,
        date,
      });
      setAmount("");
      setNote("");
      setMonth(date.slice(0, 7));
      await fetchData();
    } catch (e) {
      if (onAuthError) onAuthError(e);
    } finally {
      setSaving(false);
    }
  };

  const askDelete = async (id) => {
    if (confirmId !== id) {
      setConfirmId(id);
      setTimeout(() => setConfirmId((c) => (c === id ? "" : c)), 4000);
      return;
    }
    setConfirmId("");
    try {
      await api.delete(`/admin/ledger/${id}`);
      await fetchData();
    } catch (e) {
      if (onAuthError) onAuthError(e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="accounts-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--hoa-gold-400)]" />
      </div>
    );
  }

  const totals = data?.totals || { income: 0, expense: 0, balance: 0 };
  const entries = data?.entries || [];
  const monthEntries = entries.filter((e) => (e.date || "").startsWith(month));
  const mIncome = monthEntries.filter((e) => e.type === "income").reduce((s, e) => s + e.amount, 0);
  const mExpense = monthEntries.filter((e) => e.type === "expense").reduce((s, e) => s + e.amount, 0);

  return (
    <div className="mt-8" data-testid="accounts-view">
      <div className="flex flex-wrap gap-3">
        <SummaryCard label="Income" value={totals.income} Icon={TrendingUp} accent="#D9BD7F" testId="accounts-total-income" />
        <SummaryCard label="Expenses" value={totals.expense} Icon={TrendingDown} accent="#C97B5A" testId="accounts-total-expense" />
        <SummaryCard
          label="Balance"
          value={totals.balance}
          Icon={Scale}
          accent={totals.balance >= 0 ? "#8FBF9F" : "#C97B5A"}
          testId="accounts-total-balance"
        />
      </div>

      <div className="mt-8 rounded-[16px] border border-[rgba(179,145,74,0.3)] bg-[rgba(240,233,216,0.03)] p-5 sm:p-6" data-testid="accounts-form">
        <div className="flex rounded-full border border-[rgba(179,145,74,0.4)] overflow-hidden w-fit">
          {["income", "expense"].map((t) => (
            <button
              key={t}
              data-testid={`accounts-type-${t}`}
              onClick={() => setType(t)}
              className={`h-10 px-6 font-label text-[10px] tracking-[0.18em] uppercase transition-colors ${
                type === t
                  ? "bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)]"
                  : "text-[rgba(240,233,216,0.6)] hover:text-[var(--hoa-cream-50)]"
              }`}
            >
              {t === "income" ? "Income" : "Expense"}
            </button>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            data-testid="accounts-amount-input"
            className={inputCls}
            type="number"
            inputMode="decimal"
            min="0"
            placeholder="Amount (₹)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <input
            data-testid="accounts-date-input"
            className={`${inputCls} [color-scheme:dark]`}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        {type === "income" && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="accounts-mode-options">
            {MODES.map((m) => (
              <button
                key={m}
                data-testid={`accounts-mode-${m.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setMode(m)}
                className={`h-10 px-5 rounded-full font-label text-[10px] tracking-[0.16em] uppercase border transition-colors ${
                  mode === m
                    ? "bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] border-transparent"
                    : "border-[rgba(179,145,74,0.4)] text-[rgba(240,233,216,0.6)] hover:text-[var(--hoa-cream-50)]"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        )}

        <input
          data-testid="accounts-note-input"
          className={`${inputCls} mt-4`}
          placeholder={type === "income" ? "Note — e.g. Diksha, 2 tea cakes" : "Note — e.g. Butter, 5 kg"}
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
        />

        <button
          data-testid="accounts-add-button"
          onClick={submit}
          disabled={saving || !parseFloat(amount)}
          className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] font-label text-[11px] tracking-[0.2em] uppercase shadow-[0_10px_30px_rgba(0,0,0,0.25)] hover:brightness-110 transition-[filter] disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={1.8} />}
          Add Entry
        </button>
      </div>

      <div className="mt-8 rounded-[16px] border border-[rgba(179,145,74,0.3)] bg-[rgba(240,233,216,0.03)] overflow-hidden" data-testid="accounts-month-panel">
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-[rgba(179,145,74,0.22)]">
          <button
            data-testid="accounts-month-prev"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
            aria-label="Previous month"
            className="w-9 h-9 rounded-full border border-[rgba(179,145,74,0.35)] flex items-center justify-center text-[rgba(240,233,216,0.6)] hover:text-[var(--hoa-gold-400)] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.8} />
          </button>
          <p data-testid="accounts-month-label" className="font-display text-[var(--hoa-cream-50)] text-lg">
            {monthLabel(month)}
          </p>
          <button
            data-testid="accounts-month-next"
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
            aria-label="Next month"
            className="w-9 h-9 rounded-full border border-[rgba(179,145,74,0.35)] flex items-center justify-center text-[rgba(240,233,216,0.6)] hover:text-[var(--hoa-gold-400)] transition-colors"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>
        <div className="grid grid-cols-3 divide-x divide-[rgba(179,145,74,0.18)]">
          <div className="px-4 sm:px-5 py-3 text-center">
            <p className="font-label text-[8px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.5)]">Income</p>
            <p data-testid="accounts-month-income" className="font-display text-base sm:text-lg text-[#D9BD7F] mt-1">{inr(mIncome)}</p>
          </div>
          <div className="px-4 sm:px-5 py-3 text-center">
            <p className="font-label text-[8px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.5)]">Expenses</p>
            <p data-testid="accounts-month-expense" className="font-display text-base sm:text-lg text-[#C97B5A] mt-1">{inr(mExpense)}</p>
          </div>
          <div className="px-4 sm:px-5 py-3 text-center">
            <p className="font-label text-[8px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.5)]">Profit</p>
            <p data-testid="accounts-month-profit" className="font-display text-base sm:text-lg mt-1" style={{ color: mIncome - mExpense >= 0 ? "#8FBF9F" : "#C97B5A" }}>
              {inr(mIncome - mExpense)}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-2" data-testid="accounts-entries">
        {monthEntries.length === 0 ? (
          <div
            className="text-center py-16 border border-dashed border-[rgba(179,145,74,0.35)] rounded-[var(--hoa-radius-lg)]"
            data-testid="accounts-empty"
          >
            <p className="font-display text-[var(--hoa-cream-50)] text-xl">Nothing in {monthLabel(month)}.</p>
            <p className="font-body text-[rgba(240,233,216,0.55)] text-sm mt-2">Add an income or expense above, or browse another month.</p>
          </div>
        ) : (
          monthEntries.map((e) => (
            <div
              key={e.id}
              data-testid={`accounts-entry-${e.id}`}
              className="flex items-center gap-3 rounded-[14px] border border-[rgba(179,145,74,0.22)] bg-[rgba(240,233,216,0.03)] px-4 py-3"
            >
              <div className="flex-1 min-w-0">
                <p className="font-body text-[var(--hoa-cream-50)] text-sm truncate">
                  {e.note || (e.type === "income" ? "Income" : "Expense")}
                </p>
                <p className="font-label text-[9px] tracking-[0.16em] uppercase text-[rgba(240,233,216,0.45)] mt-0.5">
                  {fmtDate(e.date)}
                  {e.mode ? ` · ${e.mode}` : ""}
                </p>
              </div>
              <span
                className="font-display text-base sm:text-lg whitespace-nowrap"
                style={{ color: e.type === "income" ? "#D9BD7F" : "#C97B5A" }}
              >
                {e.type === "income" ? "+" : "−"} {inr(e.amount)}
              </span>
              <button
                data-testid={`accounts-delete-${e.id}`}
                onClick={() => askDelete(e.id)}
                aria-label="Delete entry"
                className={`shrink-0 inline-flex items-center gap-1 transition-colors ${
                  confirmId === e.id
                    ? "text-[#E2543A] font-label text-[9px] tracking-[0.14em] uppercase"
                    : "text-[rgba(240,233,216,0.35)] hover:text-[#E2543A]"
                }`}
              >
                {confirmId === e.id ? "Sure?" : <Trash2 className="w-4 h-4" strokeWidth={1.8} />}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
